import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { signInWithPopup } from 'firebase/auth';
import { isEmpty } from 'lodash';

import { LANGUAGES } from '../constants/languages';
import { FORGOT_PASSWORD_SUCCESS, GAME } from '../constants/routes';
import { auth, googleProvider } from '../pages/Auth/Authentication/firebase/firebase';
import { resetAll } from '../store/clearStore';
import { useUserStore } from '../store/useUserStore';
// import openNotification from '../utils/openNotification';
import routes from '../utils/routes';
import { useTranslatedNotification, getKeyByValue } from '../utils/utils';

import { AUTHENTICATION, PROFILE_API } from './constant/constant';
import { getApi, postApi, putApi, setHeaderToken } from './utils/utils';

// After login, the server only knows the language from account creation/last sync.
// If the locally selected language differs, push it so server-driven messages match the UI.
const syncLanguageWithServer = (i18nLanguage) => {
  if (!i18nLanguage || i18nLanguage === LANGUAGES.English) {
    return;
  }
  const language = getKeyByValue(LANGUAGES, i18nLanguage);
  if (language) {
    putApi(PROFILE_API.UPDATE_PROFILE, { language }, true);
  }
};

export const useLogin = (onError) => {
  const navigate = useNavigate();
  const userDetails = useUserStore();

  return useMutation({
    mutationKey: 'user_details',
    mutationFn: (data) => {
      return postApi(AUTHENTICATION.LOGIN, data);
    },
    onSuccess: (data) => {
      userDetails.updateUser(data?.data?.user);
      navigate(routes.OTP_VERIFY);
    },
    onError: (error) => {
      onError?.(error?.response?.data?.message || error?.message);
    },
  });
};

export const useRegister = (onError) => {
  const navigate = useNavigate();
  const userDetails = useUserStore();

  return useMutation({
    mutationKey: 'user_details',
    mutationFn: (data) => {
      return postApi(AUTHENTICATION.REGISTER, data);
    },
    onSuccess: (data) => {
      userDetails.updateUser(data?.data?.user);
      navigate({ pathname: routes.OTP_VERIFY }, { state: { type: 'signup' } });
    },
    onError: (error) => {
      onError?.(error?.response?.data?.message || error?.message);
    },
  });
};

export const useVerifyOtp = () => {
  const navigate = useNavigate();
  const loginStore = useUserStore();
  const { i18n } = useTranslation();

  return useMutation({
    mutationFn: (data) => {
      return postApi(AUTHENTICATION.VERIFY_OTP, data);
    },
    onSuccess: (data) => {
      setHeaderToken(data?.data?.accessToken, data?.data?.refreshToken);
      loginStore.addToken(data?.data);
      syncLanguageWithServer(i18n.language);
      navigate(GAME);
    },
    onError: () => {},
  });
};

export const useVerifyEmail = () => {
  const navigate = useNavigate();
  const loginStore = useUserStore();
  const { showNotification } = useTranslatedNotification();
  const { i18n } = useTranslation();

  return useMutation({
    mutationFn: (data) => {
      return postApi(AUTHENTICATION.VERIFY_EMAIL, data);
    },
    onSuccess: (data) => {
      showNotification('success', 'Email verified successfully');
      setHeaderToken(data?.data?.accessToken, data?.data?.refreshToken);
      loginStore.addToken(data?.data);
      syncLanguageWithServer(i18n.language);
      navigate(GAME);
    },
    onError: (error) => {
      // openNotification('error', { title: error?.response?.data?.message || error?.message });
    },
  });
};

export const useResendOtp = () => {
  const userDetails = useUserStore();

  return useQuery({
    queryKey: ['resend_otp'],
    queryFn: () => {
      return getApi(`/user/auth/resend-otp/${userDetails?.user?.id}`);
    },
    enabled: false,
    onSuccess: () => {},
    onError: (error) => {
      // openNotification('error', { title: error?.response?.data?.message || error?.message });
    },
  });
};

export const useForgotPassword = (onError) => {
  const navigate = useNavigate();
  const userDetails = useUserStore();

  return useMutation({
    mutationFn: (data) => {
      return postApi('/user/auth/forgot-password', data);
    },
    onSuccess: (data) => {
      userDetails.updateUser(data?.data?.user);
      navigate(FORGOT_PASSWORD_SUCCESS);
    },
    onError: (error) => {
      onError?.(error?.response?.data?.message || error?.message);
    },
  });
};

export const useRestorePassword = () => {
  const navigate = useNavigate();
  const { showNotification } = useTranslatedNotification();

  return useMutation({
    mutationFn: (data) => {
      return putApi(AUTHENTICATION.RESTORE, {
        ...data,
      });
    },
    onSuccess: () => {
      showNotification('success', 'Password restored successfully');
      navigate(routes.LOGIN);
    },
    onError: (error) => {
      // openNotification('error', { title: error?.response?.data?.message || error?.message });
    },
  });
};

export const useResetPassword = () => {
  const userDetails = useUserStore();
  const { showNotification } = useTranslatedNotification();

  return useMutation({
    mutationFn: (data) => {
      return putApi('/user/auth/reset-password', {
        userId: userDetails?.user?.id,
        ...data,
      });
    },
    onSuccess: () => {
      showNotification('success', 'Password changed successfully');
    },
    onError: (error) => {
      // openNotification('error', { title: error?.response?.data?.message || error?.message });
    },
  });
};

export const useManualLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return function () {
    resetAll();
    queryClient.clear();
    navigate(routes.LOGIN);
  };
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => {
      return postApi(AUTHENTICATION.LOGOUT);
    },
    onSuccess: () => {
      resetAll();
      queryClient.clear();
      navigate(routes.LOGIN);
    },
    onError: (error) => {
      // openNotification('error', { title: error?.response?.data?.message || error?.message });
    },
  });
};

export const useLoginWithGoogle = () => {
  const navigate = useNavigate();
  const loginStore = useUserStore();
  const { showNotification } = useTranslatedNotification();
  const { i18n } = useTranslation();

  return useMutation({
    mutationFn: (data) => {
      return postApi(AUTHENTICATION.GOOGLE_LOGIN, data);
    },
    onSuccess: (data) => {
      showNotification('success', 'Login successfully');
      setHeaderToken(data?.data?.accessToken, data?.data?.refreshToken);
      loginStore.addToken(data?.data);
      syncLanguageWithServer(i18n.language);
      navigate(GAME);
    },
    onError: (error) => {
      // openNotification('error', { title: error?.response?.data?.message || error?.message });
    },
  });
};

export const useGoogleFirebaseLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const loginWithGoogle = useLoginWithGoogle();

  const signInWithGoogle = async (payload) => {
    try {
      setIsLoading(true);
      const result = await signInWithPopup(auth, googleProvider);

      loginWithGoogle.mutate({
        language: 'English',
        token: result?._tokenResponse?.idToken,
        ...(!isEmpty(payload) && payload),
      });
    } catch (err) {
      console.error('Google sign-in error', err);
      alert(err.message || 'Sign in failed');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signInWithGoogle,
    isLoading: isLoading || loginWithGoogle?.isPending,
  };
};

export const useLoginWithFacebook = () => {
  const navigate = useNavigate();
  const loginStore = useUserStore();
  const { showNotification } = useTranslatedNotification();
  const { i18n } = useTranslation();

  return useMutation({
    mutationFn: (data) => {
      return postApi(AUTHENTICATION.FACEBOOK_LOGIN, data);
    },
    onSuccess: (data) => {
      showNotification('success', 'Login successfully');
      setHeaderToken(data?.data?.accessToken, data?.data?.refreshToken);
      loginStore.addToken(data?.data);
      syncLanguageWithServer(i18n.language);
      navigate(GAME);
    },
    onError: (error) => {
      // openNotification('error', { title: error?.response?.data?.message || error?.message });
    },
  });
};
